import { Link } from "react-router-dom";
import { Layers, Monitor, Shield, Cloud } from "lucide-react";
import { useContentMulti } from "@/hooks/useContent";
import { useGeoCity } from "@/hooks/useGeoCity";

const serviceSummaryConfig = [
  { icon: Layers, slugKey: "card1", slug: "managed-solutions-provider" },
  { icon: Monitor, slugKey: "card2", slug: "it-support" },
  { icon: Shield, slugKey: "card3", slug: "cyber-security" },
  { icon: Cloud, slugKey: "card4", slug: "cloud-solutions" },
];

const HomeSEOBlock = () => {
  const geoCity = useGeoCity();
  const locationLabel = geoCity || "Southern California";

  const c = useContentMulti({
    "seo.heading": "Managed IT Services and Cybersecurity in Southern California",
    "seo.para1": "Smartly Technologies is a full-service managed IT and cyber security provider serving businesses across Southern California. For over 30 years, we have helped organizations of every size simplify their technology, strengthen their security posture, and scale with confidence. Our team of engineers, developers, and security professionals works as an extension of your business, delivering the expertise and responsiveness you need without the overhead of a large internal IT department.",
    "seo.para2": "Whether you are a healthcare practice navigating HIPAA compliance, a financial services firm protecting sensitive client data, a manufacturer keeping production systems running, or a nonprofit looking for cost-effective IT support, we tailor our services to fit your industry and your goals. Our clients across Southern California rely on us for proactive monitoring, rapid helpdesk response, cloud migrations, disaster recovery planning, and ongoing security management.",
    "seo.para3": "What sets us apart is our commitment to long-term partnership. We don't just fix problems; we plan ahead, optimize your technology investments, and make sure your infrastructure evolves as your business grows. From managed IT support and vendor coordination to AI integration and custom application development, Smartly Technologies brings everything together under one roof.",
    "seo.card1_title": "Managed Solutions Provider", "seo.card1_desc": "A single trusted partner who manages your entire IT ecosystem from strategy to day-to-day operations.",
    "seo.card2_title": "IT Support", "seo.card2_desc": "24/7 proactive monitoring, helpdesk support, and infrastructure management to keep your team productive.",
    "seo.card3_title": "Cyber Security", "seo.card3_desc": "Multi-layered threat detection, compliance management, and incident response to protect your business.",
    "seo.card4_title": "Cloud Solutions", "seo.card4_desc": "Secure cloud migration, multi-cloud architecture, and ongoing management for scalable infrastructure.",
  });

  // Replace "Southern California" with detected city in content
  const replaceLocation = (text: string) =>
    text.replace(/Southern California/g, locationLabel);

  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="container mx-auto max-w-5xl">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-center mb-8 text-gray-900">
        {c["seo.heading"].includes("Southern California")
            ? <>Managed IT Services and Cybersecurity in{" "}<span className="text-primary">{locationLabel}</span></>
            : replaceLocation(c["seo.heading"])}
        </h2>

        <div className="max-w-3xl mx-auto mb-12 space-y-4 text-gray-600 text-lg leading-relaxed">
          <p>{replaceLocation(c["seo.para1"])}</p>
          <p>{replaceLocation(c["seo.para2"])}</p>
          <p>{replaceLocation(c["seo.para3"])}</p>
        </div>

        {/* Service Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {serviceSummaryConfig.map((s) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              className="group p-6 rounded-lg border border-gray-200 bg-white hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <s.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-display text-lg font-semibold mb-2 text-gray-900">{c[`seo.${s.slugKey}_title`]}</h3>
              <p className="text-sm text-gray-600">{c[`seo.${s.slugKey}_desc`]}</p>
            </Link>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/industries"
            className="text-primary font-semibold hover:underline"
          >
            See all industries we serve &rarr;
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomeSEOBlock;
